import React, { useRef, useState, useEffect } from "react";

//loaders
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import ReactECharts from "echarts-for-react";

//function reutilizables
import { fechaAExcelNumero, excelAFecha } from "../../functions/functions.js";

function FourthGraph({ visible, dataExcel, openLoader, setOpenLoader }) {
  const chartRef = useRef(null);

  const [dataGrafica, setDataGrafica] = useState({
    fechas: [],
    ton_plan: [], //eje x
    ton_real: [], //eje y
    plan_acumulado_ton: [], //eje x
    real_acumulado_ton: [], //eje y
  });

  /*filtra los registros por fecha y obtiene solos los registros que se van a ocupar
    para graficas */
  useEffect(() => {
    if (dataExcel.length > 0) {
      fetchExcel();
      setOpenLoader(false);
    }
  }, [dataExcel]);

  useEffect(() => {
    // Forzar resize cuando los datos cambian o cuando se hace visible
    if (chartRef.current && dataExcel.length > 0) {
      chartRef.current.getEchartsInstance().resize();
    }
  }, [dataExcel, visible]);

  const fetchExcel = async () => {
    try {
      //Filtramos por fecha del todo mes de junio 2025
      let fechaInicio = fechaAExcelNumero(2025, 6, 1);
      let fechaFinal = fechaAExcelNumero(2025, 6, 30);

      let _fechas = [];
      let _ton_plan = []; //eje x
      let _ton_real = []; //eje y
      let _plan_acumulado_ton = []; //eje x
      let _real_acumulado_ton = []; //eje y

      //filtra los registros por fecha y añade una columna nueva (fecha string)
      const _datosExcel = dataExcel
        .filter((row) => row[0] >= fechaInicio && row[0] <= fechaFinal)
        .map((row) => {
          _fechas.push(excelAFecha(row[0]));
          _ton_plan.push(row[3]);
          _ton_real.push(row[16]);
          _plan_acumulado_ton.push(row[2]);
          _real_acumulado_ton.push(row[15]);
        });

      setDataGrafica({
        fechas: _fechas,
        ton_plan: _ton_plan,
        ton_real: _ton_real,
        plan_acumulado_ton: _plan_acumulado_ton,
        real_acumulado_ton: _real_acumulado_ton,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        crossStyle: {
          color: "#999",
        },
      },
    },
    toolbox: {
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: function (opt) {
            const categorias = opt.xAxis[0].data;

            const seriesMap = {};
            opt.series.forEach((s) => {
              seriesMap[s.name] = s.data;
            });

            // Encabezados personalizados
            const headers = [
              "Fecha",
              "Ton (Plan)", //eje x
              "Ton (Real)",  //eje x
              "Plan Acumulado Ton",  //eje y
              "Real Acumulado Ton", //eje y
            ];

            let table = `
              <table style="width:100%;border-collapse:collapse;text-align:center;" border="1">
                <thead>
                  <tr>
                    ${headers
                      .map(
                        (h) =>
                          `<th style="padding:4px;background:#f2f2f2">${h}</th>`
                      )
                      .join("")}
                  </tr>
                </thead>
                <tbody>
            `;

            // Recorrer todas las fechas
            for (let i = 0; i < categorias.length; i++) {
              table += "<tr>";
              table += `<td>${categorias[i]}</td>`;
              table += `<td>${format(seriesMap["Ton (Plan)"]?.[i])}</td>`;
              table += `<td>${format(seriesMap["Ton (Real)"]?.[i])}</td>`;
              table += `<td>${format(seriesMap["Plan Acumulado Ton"]?.[i])}</td>`;
              table += `<td>${format(seriesMap["Real Acumulado Ton"]?.[i])}</td>`;
              table += "</tr>";
            }

            table += "</tbody></table>";
            return table;

            // Función de formato segura
            function format(val) {
              return isNaN(val) || val === null || val === ""
                ? "---"
                : parseFloat(val).toFixed(2);
            }
          },
        },

        //otros tipos de graficas
        magicType: {
          show: true,
          type: ["line", "bar"],
          title: {
            line: "Cambiar a Línea",
            bar: "Cambiar a Barra",
          },
        },
        restore: { show: true },
        saveAsImage: { show: true },
      },
    },
    //leyendas y data
    legend: {
      data: [
        "Ton (Plan)",
        "Ton (Real)",
        "Plan Acumulado Ton",
        "Real Acumulado Ton",
      ],
    },
    xAxis: [
      {
        type: "category",
        data: dataGrafica.fechas,
        axisPointer: {
          type: "shadow",
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: "TONELADAS",
        min: 0,
        max: 10000,
        interval: 1000,
        axisLabel: {
          formatter: "{value}",
        },
      },
      {
        type: "value",
        name: "Acumulado Toneladas",
        min: 0,
        max: 200000,
        interval: 20000,
        axisLabel: {
          formatter: "{value}",
        },
      },
    ],
    series: [
      {
        name: "Ton (Plan)",
        type: "bar",
        itemStyle: {
          color: "#c9c9c9",
        },
        tooltip: {
          valueFormatter: function (value) {
            return isNaN(value) || value === null || value === ""
              ? "ø"
              : parseFloat(value).toFixed(2);
          },
        },
        data: dataGrafica.ton_plan,
      },
      {
        name: "Ton (Real)",
        type: "bar",
        itemStyle: {
          color: "#ffdb96",
        },
        tooltip: {
          valueFormatter: function (value) {
            return isNaN(value) || value === null || value === ""
              ? "ø"
              : parseFloat(value).toFixed(2);
          },
        },
        data: dataGrafica.ton_real,
      },
      {
        name: "Plan Acumulado Ton",
        type: "line",
        yAxisIndex: 1,
        itemStyle: {
          color: "#7f7f7f",
        },
        lineStyle: {
          width: 3,
          type: "solid",
        },
        tooltip: {
          valueFormatter: function (value) {
            return isNaN(value) || value === null || value === ""
              ? "ø"
              : parseFloat(value).toFixed(2);
          },
        },
        data: dataGrafica.plan_acumulado_ton,
      },
      {
        name: "Real Acumulado Ton",
        type: "line",
        yAxisIndex: 1,
        itemStyle: {
          color: "#e87a30",
        },
        lineStyle: {
          width: 3,
          type: "solid",
        },
        tooltip: {
          valueFormatter: function (value) {
            return isNaN(value) || value === null || value === ""
              ? "ø"
              : parseFloat(value).toFixed(2);
          },
        },
        data: dataGrafica.real_acumulado_ton,
      },
    ],
  };

  // Manejar evento de restauración
  const onEvents = {
    restore: () => {
      // Reaplica el gráfico original
      chartRef.current.getEchartsInstance().setOption(option, true);
    },
  };

  return (
    <div className="p-4 rounded-lg shadow-lg  bg-blanco border-gray-600 mt-1 w-full">
      {/* Loader */}
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={openLoader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <div style={{ width: "100%", height: "70vh" }}>
        <ReactECharts
          ref={chartRef}
          option={option}
          onEvents={onEvents}
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    </div>
  );
}

export default FourthGraph;
